/**
 * Sanitizes a file stem (filename without extension) to ensure compatibility
 * with the system's strict naming conventions.
 * * Transformation Rules:
 * 1. Converts spaces and hyphens to underscores.
 * 2. Replaces any non-alphanumeric characters (excluding underscores and dots) with underscores.
 * 3. Collapses consecutive underscores into a single character.
 * 4. Trims leading and trailing underscores or dots.
 *
 * @param filenameStem - The raw filename stem to be sanitized.
 * @returns A sanitized string safe for use; defaults to 'file' if the result is empty.
 * @example
 * generateFilenameSuggestion("s-h-h"); // returns "s_h_h"
 * generateFilenameSuggestion("My File @2026"); // returns "My_File_2026"
 */
export function generateFilenameSuggestion(filenameStem: string): string {
    const result = filenameStem
        // 1. Turn spaces AND hyphens into underscores immediately
        .replace(/[ -]+/g, '_')

        // 2. Turn anything that isn't a letter, number, _, or . into an underscore
        // (Note: we removed the \- from this list)
        .replace(/[^a-zA-Z0-9_.]/g, '_')

        // 3. Squash multiple underscores into one (e.g., "s___h" -> "s_h")
        .replace(/_{2,}/g, '_')

        // 4. Clean up leading/trailing underscores or dots
        .replace(/^[_.]+|[_.]+$/g, '');

    return result || 'file';
}
